import { Box, Container, Link } from "@chakra-ui/react"
import LandingAnimation from "@components/animations/LandingAnimation"
import ContactForm from "@components/form/ContactForm"
import HeadingText from "@components/headings/HeadingText"
import NormalHeading from "@components/headings/NormalHeading"
import StylizedHeading from "@components/headings/StylizedHeading"
import HeaderLayout from "@components/layout/HeaderLayout"
import { GroupLogos, PartnerLogos } from "@data/logos"
import { EmenuPagesTranslation, OptionsProps } from "@definitions/types"
import { readItem, readItems } from "@directus/sdk"
import { routing } from "@i18n/routing"
import directusClient from "@utils/directus"
import { loadMessages } from "@utils/utils"
import i18nCountries from "i18n-iso-countries"
import { CountryCode } from "libphonenumber-js"
import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next"
import dynamic from "next/dynamic"
import NextLink from "next/link"
import { useRouter } from "next/router"
import { useTranslations } from "next-intl"
import { NextSeo } from "next-seo"
import { useEffect, useState } from "react"

const DynamicFooter = dynamic(() => import("@components/layout/Footer"), {
  ssr: false,
})
const DynamicGuarantee = dynamic(
  () => import("@components/animations/Guarantee"),
  { ssr: false }
)
const DynamicBenefits = dynamic(
  () => import("@components/blocks/home/Benefits"),
  {
    ssr: false,
  }
)
const DynamicQRSamples = dynamic(
  () => import("@components/blocks/home/QRSamples"),
  {
    ssr: false,
  }
)
const DynamicStats = dynamic(
  () => import("@components/blocks/home/HomeStats"),
  {
    ssr: false,
  }
)
const DynamicGroupsLogoCarousel = dynamic(
  () => import("@components/blocks/home/HomeLogoCarousel"),
  {
    ssr: false,
  }
)
const DynamicScanOrderPay = dynamic(
  () => import("@components/blocks/home/ScanOrderPay"),
  {
    ssr: false,
  }
)
const DynamicAllFeatures = dynamic(
  () => import("@components/blocks/features/AllFeatures"),
  { ssr: false }
)

const LandingPage: NextPage = ({
  locales,
  slugString,
  page_data,
  samplesList,
  countryData,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const t = useTranslations()
  const { locale } = useRouter()
  const [country, setCountry] = useState<CountryCode | null>(null)
  useEffect(() => {
    const fetchCountry = async () => {
      const response = await fetch("/api/get-country")
      const data = await response.json()
      setCountry(data.country)
    }

    fetchCountry()
  }, [])

  return (
    <>
      <NextSeo
        title={page_data.meta_title}
        description={page_data.meta_description}
        canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/lp/${slugString}`}
        languageAlternates={locales.map((lang: string) => {
          const prefix = lang === routing.defaultLocale ? "" : `/${lang}`
          return {
            hrefLang: lang,
            href: `${process.env.NEXT_PUBLIC_SITE_URL}${prefix}/lp/${slugString}`,
          }
        })}
        additionalMetaTags={[
          {
            name: "keywords",
            content: page_data.meta_keywords,
          },
        ]}
      />
      <HeaderLayout
        fullScreen
        backgroundColor={"brand.primary"}
        landingPage
      >
        <LandingAnimation />
      </HeaderLayout>

      <Container
        maxW={"container.xl"}
        px={{ base: 10, xl: 0 }}
      >
        <NormalHeading as={"h1"}>{t("contact.heading")}</NormalHeading>
        <StylizedHeading>{t("contact.sub_heading")}</StylizedHeading>
        <HeadingText>
          {t("contact.heading_text")}
          <Link
            as={NextLink}
            href={`mailto:info@digitalemenu.com`}
            borderBottomWidth={2}
            borderStyle={"dotted"}
            borderColor={"brand.primary"}
            _hover={{ textDecoration: "none", borderColor: "brand.secondary" }}
          >
            {`info@digitalemenu.com`}
          </Link>
        </HeadingText>
        {country && (
          <ContactForm
            countryList={countryData}
            defaultCountry={country}
          />
        )}

        <NormalHeading
          as={"h1"}
          color={"brand.secondary"}
        >
          {t("home.group_logos_carousel_title")}
        </NormalHeading>

        <DynamicGroupsLogoCarousel logos={GroupLogos} />

        <Box position={"relative"}>
          <NormalHeading color={"brand.secondary"}>
            {t("home.home_heading_title")}
          </NormalHeading>

          <HeadingText>{t("home.home_heading_text")}</HeadingText>

          <DynamicGuarantee
            w={40}
            h={40}
            position={{ base: "relative", xl: "absolute" }}
            right={0}
            top={0}
            m={"auto"}
            mt={{ base: 10, xl: "auto" }}
            loop={true}
            play={true}
          />

          <DynamicScanOrderPay />
        </Box>

        <NormalHeading>{t("home.benefits.digital_emenu")}</NormalHeading>
        <StylizedHeading>{t("home.benefits.benefits")}</StylizedHeading>
        <DynamicBenefits />

        <NormalHeading as={"h1"}>{t("features.add_features")}</NormalHeading>
        <StylizedHeading>{t("features.within_minutes")}</StylizedHeading>
        <HeadingText>{t("features.add_features_text")}</HeadingText>
        <DynamicAllFeatures />

        <NormalHeading>{t("home.our_leading_integrations")}</NormalHeading>
        <StylizedHeading>{t("home.partners")}</StylizedHeading>
        <DynamicGroupsLogoCarousel logos={PartnerLogos} />

        <NormalHeading>
          {t("home.flexible_design_customizations")}
        </NormalHeading>
        <StylizedHeading>{t("home.brand_guidelines")}</StylizedHeading>
        <DynamicQRSamples samplesList={samplesList} />

        <NormalHeading
          color={"brand.primary"}
          textTransform={"uppercase"}
        >
          {t("home.try")}{" "}
          <Box
            as={"span"}
            fontFamily={locale === "ar" ? "Arslan Wessam" : "Wild Spirit Alt"}
            fontWeight={400}
            fontSize={{ base: "4xl", md: "5xl", lg: "7xl" }}
            color={"brand.secondary"}
            textTransform={"lowercase"}
            p={2}
          >
            {t("home.it_on")}{" "}
          </Box>
          {t("home.your_phone")}
        </NormalHeading>
        <DynamicStats />
      </Container>
      <DynamicFooter />
    </>
  )
}

export default LandingPage

export const getStaticProps: GetStaticProps = async ({
  params,
  locale,
  locales,
}) => {
  // Country List
  const countries = i18nCountries.getNames(`${locale}`, { select: "official" })
  const countryData: OptionsProps[] = Object.keys(countries).map((key) => ({
    value: key,
    label: countries[key],
  }))
  const slug = params?.slug as string[]
  const slugString = slug.join("/")

  // Page meta data
  const page_data = await directusClient
    .request(
      readItem("emenu_pages", 1, {
        fields: ["slug", { translations: ["*"] }],
      })
    )
    .then((res) => {
      const resultTranslations = res?.translations as EmenuPagesTranslation[]
      const translations = resultTranslations?.find(
        (t) => t.languages_id === locale
      )
      return {
        slug: res?.slug,
        meta_title: translations?.meta_title,
        meta_description: translations?.meta_description,
        meta_keywords: translations?.meta_keywords,
        title: translations?.title,
        content: translations?.content,
      }
    })

  // QR Samples
  const samplesList = await directusClient.request(
    readItems("emenu_samples", {
      limit: -1,
      filter: {
        status: {
          _eq: "published",
        },
      },
      fields: [
        "id",
        "url",
        "name",
        "color",
        "image.filename_disk",
        "hover_image.filename_disk",
        "qr_code_svg.filename_disk",
      ],
    })
  )

  const [shared, home, features, contact] = await Promise.all([
    loadMessages("shared", locale ?? routing.defaultLocale),
    loadMessages("home", locale ?? routing.defaultLocale),
    loadMessages("features", locale ?? routing.defaultLocale),
    loadMessages("contact", locale ?? routing.defaultLocale),
  ])

  return {
    props: {
      locales,
      slugString,
      page_data,
      samplesList,
      countryData,
      messages: { ...shared, ...home, ...features, ...contact },
    },
    revalidate: 60, // Revalidate every 60 seconds only for landing pages
  }
}

export const getStaticPaths = async () => {
  const appLocales = [...routing.locales]

  return {
    paths: appLocales.flatMap((locale) => [
      { params: { slug: ["landing-keyword"] }, locale: locale },
      { params: { slug: ["features", "landing-keyword"] }, locale: locale },
    ]),
    fallback: "blocking",
  }
}
